import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type TechnicianProfile = components['schemas']['TechnicianProfileResponse'];
export type CreateTechnicianProfile = components['schemas']['CreateTechnicianProfileRequest'];
export type UpdateTechnicianProfile = components['schemas']['UpdateTechnicianProfileRequest'];
export type UpdateTechnicianProfileContact =
  components['schemas']['UpdateTechnicianProfileContactRequest'];

function profileQueryKey(id: string) {
  return ['technicianProfiles', id] as const;
}

/**
 * A technician's profile, looked up by USER id (M1-24) — for viewing someone
 * ELSE's profile from the roster. Requires `users.read`, so pass
 * `enabled: false` (or don't call this at all) when the caller is looking up
 * their OWN profile — a Technician holds no permission this endpoint's route
 * accepts, and would 403 on themselves; {@link useMyTechnicianProfile} is the
 * bare-authenticated path for that case. Returns `undefined`, not an error,
 * when no profile exists yet — that is a real, first-class state (an admin
 * has not created one), not a failure.
 */
export function useTechnicianProfileByUser(userId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['technicianProfiles', 'byUser', userId],
    enabled: enabled && userId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/technician-profiles', {
        params: { query: { userId: userId! } },
      });
      if (response.status === 403) {
        throw new TechnicianProfileNotPermittedError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/technician-profiles failed: ${response.status}`);
      }
      // React Query rejects `undefined` from a queryFn — `null` is this
      // hook's own "no profile yet" sentinel, same as useMyTechnicianProfile.
      return data[0] ?? null;
    },
    retry: false,
  });
}

export class TechnicianProfileNotPermittedError extends Error {
  constructor() {
    super('Not permitted to view this technician profile');
    this.name = 'TechnicianProfileNotPermittedError';
  }
}

/**
 * The signed-in user's own profile — never carries rates, whatever the
 * caller's role (api/CONVENTIONS.md). Which is why a caller who holds
 * `users.manage` should reach even their OWN profile through
 * {@link useTechnicianProfileByUser} instead: this response cannot seed an
 * edit form without saving its own blanked-out rates back. Gated by
 * `enabled` for that reason rather than fired unconditionally.
 */
export function useMyTechnicianProfile(enabled = true) {
  return useQuery({
    queryKey: ['technicianProfiles', 'me'],
    enabled,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/technician-profiles/me');
      if (response.status === 404) {
        return null;
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/technician-profiles/me failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

function invalidateProfile(
  queryClient: ReturnType<typeof useQueryClient>,
  profile: TechnicianProfile,
) {
  void queryClient.invalidateQueries({ queryKey: profileQueryKey(profile.id) });
  void queryClient.invalidateQueries({
    queryKey: ['technicianProfiles', 'byUser', profile.userId],
  });
  void queryClient.invalidateQueries({ queryKey: ['technicianProfiles', 'me'] });
}

export function useCreateTechnicianProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateTechnicianProfile) => {
      const { data, response } = await apiClient.POST('/api/v1/technician-profiles', {
        body: request,
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/technician-profiles failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: (profile) => invalidateProfile(queryClient, profile),
  });
}

/** The full edit — owner/admin only, enforced server-side. */
export function useUpdateTechnicianProfile(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateTechnicianProfile) => {
      const { data, response } = await apiClient.PUT('/api/v1/technician-profiles/{id}', {
        params: { path: { id } },
        body: request,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/technician-profiles/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: (profile) => invalidateProfile(queryClient, profile),
  });
}

/** The ONE thing a technician may write on their own profile. */
export function useUpdateTechnicianProfileContact(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateTechnicianProfileContact) => {
      const { data, response } = await apiClient.PUT('/api/v1/technician-profiles/{id}/contact', {
        params: { path: { id } },
        body: request,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/technician-profiles/{id}/contact failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: (profile) => invalidateProfile(queryClient, profile),
  });
}

const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Uploads a technician's profile photo (M1-24) — request a SAS, PUT straight
 * to Blob Storage, confirm. A single file and no queue, unlike
 * `useUploadJobPhotos`: a profile has one photo, not a gallery, so there is
 * nothing to track across multiple concurrent uploads.
 */
export function useUploadTechnicianPhoto(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
        throw new Error(`Unsupported file type: ${file.type}`);
      }

      const requested = await apiClient.POST('/api/v1/technician-profiles/{id}/photo', {
        params: { path: { id } },
        body: { contentType: file.type },
      });
      if (!requested.response.ok || !requested.data) {
        throw new Error(`request-upload failed: ${requested.response.status}`);
      }

      const uploaded = await fetch(requested.data.uploadUrl, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
        body: file,
      });
      if (!uploaded.ok) {
        throw new Error(`blob upload failed: ${uploaded.status}`);
      }

      const completed = await apiClient.POST('/api/v1/technician-profiles/{id}/photo/complete', {
        params: { path: { id } },
      });
      if (!completed.response.ok || !completed.data) {
        throw new Error(`complete failed: ${completed.response.status}`);
      }
      return completed.data;
    },
    onSuccess: (profile) => invalidateProfile(queryClient, profile),
  });
}
